import { Button } from '@mui/material';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { IS_AI_SUGGEST_ENABLED } from '../../constants/ai';
import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID, EMPTY_CUSTOM_SEND } from '../../constants/bucket';
import { DEPARTMENT_IDS, MAX_CAMPAIGN_ITEMS_PER_PAGE } from '../../constants/campaigns';
import { FULFILLMENT_CENTER_ID } from '../../constants/inventories';
import { OUT_OF_DEPARTMENT_MONEY_MESSAGE, OUT_OF_MONEY_MESSAGE } from '../../constants/shell';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../constants/users';
import { useCurrency } from '../../contexts/CurrencyProvider';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import useDeptFilter from '../../hooks/useDeptFilter';
import useFulfillmentCenterFilter from '../../hooks/useFulfillmentCentersFilter';
import useWindowSize from '../../hooks/useWindowSize';
import {
  addBucketValue,
  pickYourGiftBoxesConfirmed,
  preSelectedBoxConfirmed,
  setPYG,
  togglePYG,
} from '../../store/actions/bucket';
import { fetchCampaignsRequest } from '../../store/actions/campaigns';
import { selectAdminType, selectRemainingBudget } from '../../store/selectors/auth';
import { selectIsPYGEnabled, selectPYGCampaignsList } from '../../store/selectors/bucket';
import { selectCampaignsByType, selectCampaignsCount, selectIsFinalCampaign } from '../../store/selectors/campaigns';
import {
  selectCurrentDepartmentRemainingBudget,
  selectIsAIGiftSuggestionEnabled,
  selectIsDepartmentBudgetUnlimited,
  selectIsUserBudgetEnabled,
} from '../../store/selectors/organizations';
import { selectIsShowDepartmentBudget } from '../../store/selectors/shell';
import { IFlowStepProps } from '../../types/bucket';
import { ICampaign, ICustomSend, IFetchCampaignsRequest } from '../../types/campaigns';

import { INITIAL_PAGE } from '../../constants/table';
import BoxDetailsSidebar from '../../containers/BoxDetailsSidebar/BoxDetailsSidebar';
import useSearchFilter from '../../hooks/useSearchFilter/useSearchFilter';
import { toggleUiElement } from '../../store/actions/shell';
import { BoxDetailsSidebarModeEnum, NotificationListEnum } from '../../types/shell';
import { isCampaignEligibleToOrder } from '../../utils/campaigns';
import { isSameFC } from '../../utils/inventories';
import notification from '../../utils/notification';
import { hasPermission } from '../../utils/users';
import BoxItem from '../BoxItem/BoxItem';
import { ActionButton, PickYourGiftButton } from '../forms';
import { CustomSendItem } from '../index';
import PaginatedList, { IPaginatedListChangeEvent, IPaginatedListRef } from '../PaginatedList/PaginatedList';
import { PYGEditSidebar } from '../PYGEditSidebar/PYGEditSidebar';
import { OutOfMoneyToast } from '../toast';
import { MuiFloatingButton } from '../_mui';

import styles from './BoxList.module.scss';

const BoxList: React.FC<IFlowStepProps> = (flowProps) => {
  const { onBoxSelect, next: nextStep, isBulk, isSingle } = flowProps;

  const dispatch = useDispatch();
  const history = useHistory();
  const { setAdditionalData, setClassName: setHeaderClassName } = useHeaderDispatch();
  const { mobile } = useWindowSize();
  const { getCampaignTotal, getSendTotalPrice } = useCurrency();

  const [selectedBox, setSelectedBox] = useState<ICampaign | undefined>();
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const adminType = useSelector(selectAdminType);
  const isPYGEnabled = useSelector(selectIsPYGEnabled);
  const pygCampaignsList = useSelector(selectPYGCampaignsList);
  const hasDepartmentBudget = useSelector(selectIsShowDepartmentBudget);
  const isDepartmentBudgetUnlimited = useSelector(selectIsDepartmentBudgetUnlimited);
  const departmentBudget = useSelector(selectCurrentDepartmentRemainingBudget);
  const boxItems = useSelector(selectCampaignsByType());
  const boxItemsCount = useSelector(selectCampaignsCount);
  const isFinalCampaign = useSelector(selectIsFinalCampaign);
  const remainingUserBudget = useSelector(selectRemainingBudget);
  const isUserBudgetEnabled = useSelector(selectIsUserBudgetEnabled);
  const isAIGiftSuggestionEnabled = useSelector(selectIsAIGiftSuggestionEnabled);

  const boxListRef = useRef<HTMLDivElement>(null);

  const debouncedPageChange = useMemo(
    () =>
      debounce(() => {
        if (paginatedListRef.current) {
          paginatedListRef.current.changePage(1);
        }
      }, 1000),
    [],
  );

  const { SearchInput, query } = useSearchFilter({
    items: [],
    fieldNames: [],
    props: {
      className: styles.searchByFilterContainer,
      hint: 'Look up specific campaigns by their name.',
      dataAction: 'search_input',
    },
    onChange: debouncedPageChange,
  });

  const {
    departments: selectedDepartment,
    DeptFilter,
    filteredItems,
  } = useDeptFilter<ICampaign>((boxItems as ICampaign[]) || [], DEPARTMENT_IDS, {
    className: styles.dropdownFilter,
    contentClassName: styles.dropdownContent,
    labelClassName: styles.dropdownLabel,
    dataAction: 'filter_dept',
  });

  const {
    FulfillmentCenterFilter,
    filteredItems: filteredItemsByFC,
    fulfillmentCenters: selectedFulfillmentCenter,
  } = useFulfillmentCenterFilter<ICampaign>(filteredItems || [], FULFILLMENT_CENTER_ID, {
    className: styles.dropdownFilter,
    contentClassName: styles.dropdownContent,
    labelClassName: styles.dropdownLabel,
    dataAction: 'filter_fc',
  });

  const paginatedListRef = useRef<IPaginatedListRef<ICampaign>>(null);

  const shouldShowContactAdminMessage = useMemo(
    () => !hasPermission([SUPER_ADMIN, DISTRIBUTOR, ORG_ADMIN], adminType),
    [adminType],
  );

  const getRequestInfo = useCallback(
    (payload: IFetchCampaignsRequest) => {
      return {
        ...(selectedDepartment && { department_id: selectedDepartment as string }),
        ...(query && { search_query: query }),
        ...(selectedFulfillmentCenter && { fulfillment_center_id: selectedFulfillmentCenter as string }),
        ...payload,
      };
    },
    [query, selectedFulfillmentCenter, selectedDepartment],
  );

  const fetchCampaigns = useCallback(
    (payload: IFetchCampaignsRequest) => {
      dispatch(fetchCampaignsRequest(getRequestInfo(payload)));
    },
    [dispatch, getRequestInfo],
  );

  const handleCloseSidebar = useCallback(() => {
    setSelectedBox(undefined);
    onBoxSelect?.(null);
  }, [onBoxSelect]);

  const setPYGValue = useCallback(
    (campaign: ICampaign | ICustomSend) => {
      if (campaign.box_id !== CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID) {
        dispatch(setPYG(campaign as ICampaign));
      }

      handleCloseSidebar();
    },
    [dispatch, handleCloseSidebar],
  );

  const campaignIsNotEligibleToOrder = useMemo(() => {
    return !isCampaignEligibleToOrder(selectedBox?.items);
  }, [selectedBox]);

  const handleTogglePYG = useCallback(() => {
    dispatch(togglePYG(!isPYGEnabled));
  }, [dispatch, isPYGEnabled]);

  const handleToggleSuggestGift = useCallback(() => {
    dispatch(toggleUiElement({ suggestGiftSidenavIsShown: true }));
  }, [dispatch]);

  const setHeaderAdditionalData = useCallback(() => {
    if (!setAdditionalData) {
      return;
    }

    setAdditionalData(
      <div className={cn(styles.headerAdditionalContent, { [styles.pygEnabled]: isPYGEnabled })}>
        {IS_AI_SUGGEST_ENABLED && isSingle && isAIGiftSuggestionEnabled && (
          <ActionButton
            className={styles.suggestGiftBtn}
            title="Suggest gifts with AI"
            onClick={handleToggleSuggestGift}
          />
        )}
        <PickYourGiftButton className={styles.pygEnableBtn} isEnabled={isPYGEnabled} onClick={handleTogglePYG} />
      </div>,
    );

    // Need to clean additional information after yourself
    return setAdditionalData;
  }, [setAdditionalData, isPYGEnabled, isSingle, handleToggleSuggestGift, handleTogglePYG, isAIGiftSuggestionEnabled]);

  const setMobileHeaderClassName = useCallback(() => {
    if (mobile) {
      setHeaderClassName?.(cn(styles.header, { [styles.enabled]: isPYGEnabled }));
    }

    return setHeaderClassName || undefined;
  }, [setHeaderClassName, mobile, isPYGEnabled]);

  const handleOpenSidebar = useCallback(
    (box: ICampaign) => {
      setSelectedBox(box);
      onBoxSelect?.(box);
    },
    [setSelectedBox, onBoxSelect],
  );

  /**
   * Taking price and triggering warning if it is
   * less than department budget or individual budget.
   * Department budget takes precedence of the individual budget
   * @param {number} price - price of chosen campaign or campaigns
   * @return {void}
   */
  const checkBudgets = useCallback(
    (price: number) => {
      if (!isBulk && hasDepartmentBudget && !isDepartmentBudgetUnlimited && price > departmentBudget.amount) {
        notification.outOfMoney(
          <OutOfMoneyToast showContactAdminMessage={shouldShowContactAdminMessage}>
            {OUT_OF_DEPARTMENT_MONEY_MESSAGE}
          </OutOfMoneyToast>,
        );
        return void 0;
      }
      if (isUserBudgetEnabled && remainingUserBudget < price && !isBulk) {
        notification.outOfMoney(
          <OutOfMoneyToast showContactAdminMessage={shouldShowContactAdminMessage}>
            {OUT_OF_MONEY_MESSAGE}
          </OutOfMoneyToast>,
        );
        return void 0;
      }

      return void 0;
    },
    [
      hasDepartmentBudget,
      departmentBudget,
      remainingUserBudget,
      isDepartmentBudgetUnlimited,
      shouldShowContactAdminMessage,
      isBulk,
      isUserBudgetEnabled,
    ],
  );

  const handleBoxSubmit = useCallback(
    (box: ICampaign | ICustomSend) => {
      dispatch(preSelectedBoxConfirmed(box, flowProps));
      checkBudgets(getCampaignTotal(box).price);

      if (nextStep) {
        history.push(nextStep);
        checkBudgets(getCampaignTotal(box).price);
      }
    },
    [dispatch, flowProps, checkBudgets, nextStep, history, getCampaignTotal],
  );

  const handleSidebarContinue = useCallback(() => {
    if (!pygCampaignsList || !pygCampaignsList.length) {
      notification.error(NotificationListEnum.PYGCampaignIsNotSelected, {
        content: 'Cannot proceed with "Pick your gift" option selected with no Campaigns picked.',
      });
      return;
    }
    setSidebarOpen(true);
  }, [pygCampaignsList]);

  const sidebarCampaigns = useMemo(() => pygCampaignsList ?? [], [pygCampaignsList]);

  const handleCampaignsSidebarSave = useCallback(() => {
    const finalCampaigns = pygCampaignsList;

    if (!finalCampaigns || !finalCampaigns.length) {
      return;
    }

    setSidebarOpen(false);

    if (finalCampaigns.length > 1) {
      const totalCost = getSendTotalPrice({ pick_campaigns: finalCampaigns });
      checkBudgets(totalCost.price);

      dispatch(pickYourGiftBoxesConfirmed(finalCampaigns, flowProps));
      if (nextStep) {
        history.push(nextStep);
      }

      return;
    }

    handleBoxSubmit(finalCampaigns[0]);
  }, [flowProps, pygCampaignsList, handleBoxSubmit, checkBudgets, dispatch, nextStep, history, getSendTotalPrice]);

  const handleCampaignsSidebarCancel = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleCampaignsReorder = useCallback(
    (next: ICampaign[]) => {
      dispatch(addBucketValue({ pick_campaigns: next }));
    },
    [dispatch],
  );

  const handleCampaignRemove = useCallback(
    (campaign: ICampaign) => {
      dispatch(setPYG(campaign));
    },
    [dispatch],
  );

  const handlePaginationChange = useCallback(
    ({ page, setPage }: IPaginatedListChangeEvent) => {
      fetchCampaigns({ page, page_size: MAX_CAMPAIGN_ITEMS_PER_PAGE });
      // In case there's an error on the endpoint, paginator will change the page number, which is not correct.
      // The fix is to wrap the request with the Promise and call the `setPage(page)` after the Promise is resolved
      setPage(page);
    },
    [fetchCampaigns],
  );

  const handleCustomizeCampaign = useCallback(
    ({ items, name, description }: ICampaign) => {
      dispatch(
        preSelectedBoxConfirmed({ name, description, items, box_id: CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID }, flowProps),
      );
      dispatch(toggleUiElement({ boxDetailsSidebarIsShown: true }));
    },
    [dispatch, flowProps],
  );

  const renderBoxItem = useCallback(
    (box: ICampaign, i: number) => {
      const { box_id: boxId, items } = box;
      const isBoxDisabled = !isSameFC(items) || (!isCampaignEligibleToOrder(items) && isPYGEnabled);

      return (
        <div className={styles.itemWrapper} key={boxId}>
          <BoxItem
            key={boxId}
            box={box}
            onClick={() => handleOpenSidebar(box)}
            onSelect={isPYGEnabled ? () => setPYGValue(box) : undefined}
            selected={pygCampaignsList?.some(({ box_id: pygCampaignBoxId }) => pygCampaignBoxId === boxId)}
            className={styles.item}
            iconClassName={styles.icon}
            withPreview
            withWarning
            isDisabled={isBoxDisabled}
            dataIndex={i}
          />
        </div>
      );
    },
    [isPYGEnabled, pygCampaignsList, handleOpenSidebar, setPYGValue],
  );

  const handleCustomBoxClick = useCallback(() => {
    dispatch(preSelectedBoxConfirmed(EMPTY_CUSTOM_SEND, flowProps));
  }, [dispatch, flowProps]);

  useEffect(() => {
    if (isSidebarOpen && !pygCampaignsList?.length) {
      setSidebarOpen(false);
    }
  }, [isSidebarOpen, pygCampaignsList]);

  useEffect(() => {
    fetchCampaigns({ page: INITIAL_PAGE, page_size: MAX_CAMPAIGN_ITEMS_PER_PAGE });
  }, [fetchCampaigns]);

  useEffect(() => {
    return setHeaderAdditionalData();
  }, [setHeaderAdditionalData]);

  useEffect(() => {
    return setMobileHeaderClassName();
  }, [setMobileHeaderClassName]);

  const buttonConfig = useMemo(() => {
    if (isPYGEnabled) {
      const isBoxSelected = pygCampaignsList?.some((box) => box.box_id === selectedBox?.box_id);
      return isBoxSelected
        ? { title: 'Remove campaign', color: 'error' as const }
        : { title: 'Add campaign', color: 'primary' as const };
    }

    return { title: 'Continue', color: 'primary' as const };
  }, [isPYGEnabled, pygCampaignsList, selectedBox]);

  const isButtonOpen = useMemo(
    () => isPYGEnabled && (pygCampaignsList?.length || 0) > 0 && !selectedBox,
    [isPYGEnabled, pygCampaignsList],
  );

  const renderActionButtons = useMemo(() => {
    if (selectedBox) {
      const handleClick = (() => {
        return isPYGEnabled ? setPYGValue : handleBoxSubmit;
      })();

      const isContinueButtonDisabled =
        !Boolean(selectedBox && selectedBox.items && selectedBox.items.length) || campaignIsNotEligibleToOrder;

      return [
        <Button
          key="continueButton"
          variant="contained"
          size="small"
          onClick={() => handleClick(selectedBox)}
          color={buttonConfig.color}
          disabled={isContinueButtonDisabled}
          data-action="continue_btn"
          sx={{ fontSize: 15, lineHeight: 1.7, py: 1, px: 2.75 }}
        >
          {buttonConfig.title}
        </Button>,
      ];
    }
  }, [selectedBox, isPYGEnabled, setPYGValue, handleBoxSubmit, buttonConfig, campaignIsNotEligibleToOrder]);

  return (
    <div className={styles.boxList}>
      <div className={styles.wrapper}>
        <div className={styles.filter}>
          {SearchInput}
          <div className={styles.commonFilters}>
            {hasPermission([ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN], adminType) ? DeptFilter({}) : null}
            {FulfillmentCenterFilter({ isFiltered: true })}
          </div>
        </div>
      </div>
      <PaginatedList<ICampaign>
        totalCount={boxItemsCount}
        ref={paginatedListRef}
        items={filteredItemsByFC || ([] as ICampaign[])}
        pageSize={MAX_CAMPAIGN_ITEMS_PER_PAGE}
        onChange={handlePaginationChange}
        isFinalItems={isFinalCampaign}
        scrollContainerRef={boxListRef}
      >
        {({ items, pagination: Pagination }) => (
          <>
            <div className={styles.listRow} ref={boxListRef}>
              {!isPYGEnabled && (
                <div className={styles.itemWrapper}>
                  <CustomSendItem className={styles.item} onClick={handleCustomBoxClick} />
                </div>
              )}
              {items && items.length ? items.map(renderBoxItem) : null}
            </div>
            <Pagination className={styles.pagination} />
          </>
        )}
      </PaginatedList>
      <MuiFloatingButton
        text="Continue"
        count={pygCampaignsList?.length || 0}
        visible={isButtonOpen}
        disabled={!pygCampaignsList?.length}
        onClick={handleSidebarContinue}
        dataAction="continue_btn"
      />
      {isSidebarOpen && sidebarCampaigns.length > 0 && (
        <PYGEditSidebar
          campaigns={sidebarCampaigns}
          onReorder={handleCampaignsReorder}
          onRemove={handleCampaignRemove}
          onCancel={handleCampaignsSidebarCancel}
          onSave={handleCampaignsSidebarSave}
        />
      )}
      <BoxDetailsSidebar
        onSubmit={isPYGEnabled ? setPYGValue : handleBoxSubmit}
        onClose={handleCloseSidebar}
        isBoxSelected={pygCampaignsList?.some((box) => box.box_id === selectedBox?.box_id)}
        box={selectedBox}
        isPYGEnabled={isPYGEnabled}
        actionButtons={renderActionButtons}
        mode={BoxDetailsSidebarModeEnum.View}
        onCustomize={handleCustomizeCampaign}
      />
    </div>
  );
};

export default BoxList;
